import { useShelvingBoardHover, useShelvingUprightFocus } from "../store/useShelvingBoardHover";
import { useSlideOut } from "./SlideOutPanel";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import {
  APP_VERSION,
  BUILD_ID,
  BUILD_LABEL,
  reloadWithFreshAssets,
  runtimeDiagnostics
} from "../buildInfo";
import { usePlannerStore } from "../store/usePlannerStore";
import { canEditProduct, productFamilyFor, type ProductFamily } from "../domain/productWorkflow";
import {
  PlannerScene,
  type PlannerSceneAssetWarning,
  type PlannerSceneIssue
} from "../three/PlannerScene";

export interface PlannerCanvasHandle {
  setView: (view: "front" | "perspective") => void;
  setDimensionsVisible: (visible: boolean) => void;
  setInteriorPropsVisible: (visible: boolean) => void;
  setPresentationMode: (enabled: boolean) => void;
  resetCamera: () => void;
  capture: () => void;
  getSnapshot: () => string | null;
  /** Картинка проекта для карточки в кабинете: общий вид изделия, 2 : 1. */
  getProjectPreview: () => string | null;
  getProjectRenderSnapshot: () => string | null;
  getProjectRenderReferenceSnapshot: () => string | null;
  getProjectRenderStateSpecification: () => string;
}

export type PlannerCanvasSelectionTarget =
  | "modules"
  | "facades"
  | "internals";

type PlannerCanvasProps = {
  onSelectionIntent?: (target: PlannerCanvasSelectionTarget) => void;
  presentationMode?: boolean;
  productFamily: ProductFamily | null;
  onBlockedSelection?: (family: ProductFamily) => void;
};

type SceneState =
  | {
      kind: "loading";
      title: string;
      message: string;
    }
  | { kind: "ready" }
  | {
      kind: "error";
      title: string;
      message: string;
      code: string;
      technicalMessage?: string;
    };

const loadingState = (): SceneState => ({
  kind: "loading",
  title: "Готовим 3D-комнату",
  message: "Проверяем графику, материалы и сохранённый проект…"
});

function detectWebGlProblem(): SceneState | null {
  if (!navigator.onLine) {
    return {
      kind: "error",
      title: "Нет соединения с интернетом",
      message:
        "Подключитесь к сети и обновите приложение. Сохранённый проект останется на месте.",
      code: "OFFLINE"
    };
  }

  try {
    const canvas = document.createElement("canvas");
    const context =
      canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: false }) ??
      canvas.getContext("webgl", { failIfMajorPerformanceCaveat: false });

    if (!context) {
      return {
        kind: "error",
        title: "3D недоступно в этом браузере",
        message:
          "Откройте конфигуратор в актуальном Chrome или Edge и включите аппаратное ускорение.",
        code: "WEBGL_UNAVAILABLE"
      };
    }

    context.getExtension("WEBGL_lose_context")?.loseContext();
    return null;
  } catch (error) {
    return {
      kind: "error",
      title: "Не удалось проверить 3D",
      message:
        "Браузер заблокировал графику. Обновите страницу или включите аппаратное ускорение.",
      code: "WEBGL_CHECK_FAILED",
      technicalMessage: error instanceof Error ? error.message : String(error)
    };
  }
}

function stateFromSceneIssue(issue: PlannerSceneIssue): SceneState {
  return {
    kind: "error",
    title:
      issue.code === "context-lost"
        ? "3D-сцена временно остановлена"
        : "Не удалось отрисовать комнату",
    message: issue.message,
    code: issue.code.toUpperCase().replaceAll("-", "_"),
    technicalMessage: issue.technicalMessage
  };
}

export const PlannerCanvas = forwardRef<
  PlannerCanvasHandle,
  PlannerCanvasProps
>(
  function PlannerCanvas({ onSelectionIntent, presentationMode = false, productFamily, onBlockedSelection }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<PlannerScene | null>(null);
    const onSelectionIntentRef = useRef(onSelectionIntent);
    const presentationModeRef = useRef(presentationMode);
    const productFamilyRef = useRef(productFamily);
    const onBlockedSelectionRef = useRef(onBlockedSelection);
    productFamilyRef.current = productFamily;
    onBlockedSelectionRef.current = onBlockedSelection;
    const dimensionsVisibleRef = useRef(true);
    const interiorPropsVisibleRef = useRef(true);
    presentationModeRef.current = presentationMode;
    const [restartToken, setRestartToken] = useState(0);
    const [sceneState, setSceneState] = useState<SceneState>(loadingState);
    const [assetWarnings, setAssetWarnings] = useState<
      PlannerSceneAssetWarning[]
    >([]);
    const [doorObstruction, setDoorObstruction] = useState<string | null>(null);
    const project = usePlannerStore((state) => state.project);

    useEffect(() => {
      onSelectionIntentRef.current = onSelectionIntent;
    }, [onSelectionIntent]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      let active = true;
      let readyTimer = 0;
      const earliestReadyAt = performance.now() + 520;
      setSceneState(loadingState());
      setAssetWarnings([]);
      setDoorObstruction(null);

      let unsubscribeBoardHover: (() => void) | undefined;
      let unsubscribeSlideOut: (() => void) | undefined;
      const webGlProblem = detectWebGlProblem();
      if (webGlProblem) {
        setSceneState(webGlProblem);
        return () => {
          active = false;
        };
      }

      try {
        const scene = new PlannerScene(container, {
          canInteractWithModule: moduleId => {
            const module = usePlannerStore.getState().project.modules.find(item => item.id === moduleId);
            if (!module) return false;
            if (canEditProduct(module, productFamilyRef.current)) return true;
            onBlockedSelectionRef.current?.(productFamilyFor(module));
            return false;
          },
          onReady: () => {
            const delay = Math.max(0, earliestReadyAt - performance.now());
            readyTimer = window.setTimeout(() => {
              if (active) setSceneState({ kind: "ready" });
            }, delay);
          },
          onError: (issue) => {
            if (active) setSceneState(stateFromSceneIssue(issue));
          },
          onDoorObstruction: message => {
            if (active) setDoorObstruction(message);
          },
          onAssetWarning: (warning) => {
            if (!active) return;
            setAssetWarnings((current) =>
              current.some((item) => item.resource === warning.resource)
                ? current
                : [...current, warning]
            );
          },
          onSelectModule: (moduleId, source) => {
            usePlannerStore.getState().selectModule(moduleId);
            onSelectionIntentRef.current?.(
              source === "facade" ? "facades" : "modules"
            );
          },
          // Щелчок по пустому месту снимает выбор полки или ячейки, но изделие
          // остаётся выбранным — шаг в панели не сбрасывается (приёмка 11.10, wd-06).
          onClearSelection: () => {
            const state = usePlannerStore.getState();
            const selectedId = state.project.selectedModuleId;
            if (selectedId && state.project.modules.some((module) => module.id === selectedId)) {
              if (state.project.selectedItemId || state.project.selectedShelvingCellId) state.selectModule(selectedId);
              return;
            }
            state.clearSelection();
          },
          // Выезжающая панель (ячейки, каталог декоров) закрывается щелчком по пустому месту 3D.
          onEmptyClick: () => { if (useSlideOut.getState().owner) useSlideOut.setState({ owner: null }); },
          onCommitModulePosition: (moduleId, wallOffset) =>
            usePlannerStore.getState().moveModule(moduleId, wallOffset),
          onCommitModuleUpdate: (moduleId, patch) =>
            usePlannerStore.getState().updateModule(moduleId, patch),
          onCommitShelvingCellUpdate: (moduleId, cellId, patch) =>
            usePlannerStore
              .getState()
              .updateShelvingCell(moduleId, cellId, patch),
          onHoverShelvingBoard: (hovered) =>
            useShelvingBoardHover.getState().setHovered(hovered),
          onClickShelvingUpright: (moduleId, dividerIndex, row) => {
            // У комода стойки не поворачиваются, панели ячеек нет.
            if (productFamilyRef.current !== "shelving") return;
            useShelvingUprightFocus.getState().setFocus({ moduleId, dividerIndex, row });
            // Щелчок по стойке — осознанный: открываем панель с её поворотом.
            useSlideOut.getState().open("shelving-cells");
          },
          onSelectItem: (moduleId, itemId) => {
            usePlannerStore.getState().selectItem(moduleId, itemId);
            onSelectionIntentRef.current?.("internals");
          },
          onSelectShelvingCell: (moduleId, cellId, source) => {
            // Взялись за полку или стойку, открывают ящик — панель ячеек не открываем,
            // чтобы она не закрыла 3D; шаг в боковой панели тоже не переключаем.
            if (source !== "cell" && usePlannerStore.getState().project.selectedShelvingCellId !== cellId) {
              useSlideOut.getState().markQuiet();
            }
            usePlannerStore.getState().selectShelvingCell(moduleId, cellId);
            if (source === "cell") onSelectionIntentRef.current?.("internals");
          },
          onCommitItemPosition: (moduleId, itemId, y) =>
            usePlannerStore.getState().updateItem(moduleId, itemId, { y }),
          onCommitItemPieceMove: (moduleId, itemId, x, y) =>
            usePlannerStore.getState().moveItemInCompartment(moduleId, itemId, x, y),
          onCommitItemUpdate: (moduleId, itemId, patch) =>
            usePlannerStore.getState().updateItem(moduleId, itemId, patch)
        });
        sceneRef.current = scene;
        scene.setProject(usePlannerStore.getState().project);
        // Подсветка детали ячейки: из 3D и из списка в панели ячеек.
        unsubscribeBoardHover = useShelvingBoardHover.subscribe((state) =>
          sceneRef.current?.setHoveredBoard(state.hovered));
        // Открытая панель ячеек: 3D сдвигается влево, изделие не прячется под ней.
        unsubscribeSlideOut = useSlideOut.subscribe((state, previous) => {
          if (state.inset !== previous.inset) sceneRef.current?.setViewInset(state.inset);
        });
        scene.setViewInset(useSlideOut.getState().inset);
        scene.setDimensionsVisible(dimensionsVisibleRef.current);
        scene.setInteriorPropsVisible(interiorPropsVisibleRef.current);
        scene.setPresentationMode(presentationModeRef.current);
      } catch (error) {
        console.error("Unable to initialize planner scene", error);
        setSceneState({
          kind: "error",
          title: "Не удалось запустить 3D",
          message:
            "Обновите приложение. Если ошибка повторится, включите аппаратное ускорение браузера.",
          code: "SCENE_INITIALIZATION_FAILED",
          technicalMessage:
            error instanceof Error ? error.message : String(error)
        });
      }

      return () => {
        active = false;
        window.clearTimeout(readyTimer);
        unsubscribeBoardHover?.();
        unsubscribeSlideOut?.();
        sceneRef.current?.dispose();
        sceneRef.current = null;
      };
    }, [restartToken]);

    useEffect(() => {
      if (!sceneRef.current) return;
      try {
        sceneRef.current.setProject(project);
      } catch (error) {
        console.error("Unable to update planner scene", error);
        setSceneState({
          kind: "error",
          title: "Не удалось обновить 3D-сцену",
          message:
            "Проект сохранён. Попробуйте перезапустить 3D или обновить приложение.",
          code: "SCENE_UPDATE_FAILED",
          technicalMessage:
            error instanceof Error ? error.message : String(error)
        });
      }
    }, [project]);

    useEffect(() => {
      sceneRef.current?.setPresentationMode(presentationMode);
    }, [presentationMode]);

    useImperativeHandle(
      ref,
      () => ({
        setView: (view) => sceneRef.current?.setView(view),
        setDimensionsVisible: (visible) => {
          dimensionsVisibleRef.current = visible;
          sceneRef.current?.setDimensionsVisible(visible);
        },
        setInteriorPropsVisible: (visible) => {
          interiorPropsVisibleRef.current = visible;
          sceneRef.current?.setInteriorPropsVisible(visible);
        },
        setPresentationMode: (enabled) =>
          sceneRef.current?.setPresentationMode(enabled),
        resetCamera: () => sceneRef.current?.resetCamera(),
        capture: () => sceneRef.current?.capture(),
        getSnapshot: () => sceneRef.current?.getSnapshot() ?? null,
        getProjectPreview: () => sceneRef.current?.getProjectPreview() ?? null,
        getProjectRenderSnapshot: () =>
          sceneRef.current?.getProjectRenderSnapshot() ?? null,
        getProjectRenderReferenceSnapshot: () =>
          sceneRef.current?.getAiReferenceSnapshot("back") ?? null,
        getProjectRenderStateSpecification: () =>
          sceneRef.current?.getProjectRenderStateSpecification() ?? ""
      }),
      []
    );

    const retryScene = () => setRestartToken((token) => token + 1);
    const currentError = sceneState.kind === "error" ? sceneState : null;
    const diagnosticText = currentError
      ? runtimeDiagnostics(
          `${currentError.code}: ${currentError.technicalMessage ?? currentError.message}`
        )
      : "";

    return (
      <div className="canvas-shell" ref={containerRef}>
        {sceneState.kind !== "ready" && (
          <div
            className={`scene-state-layer scene-state-layer--${sceneState.kind}`}
            role={sceneState.kind === "error" ? "alert" : "status"}
            aria-live="polite"
          >
            <section className="scene-state-card">
              <div
                className={
                  sceneState.kind === "loading"
                    ? "scene-state-card__spinner"
                    : "scene-state-card__warning"
                }
                aria-hidden="true"
              >
                {sceneState.kind === "error" ? "!" : ""}
              </div>
              <span className="scene-state-card__eyebrow">
                Egel Design Planner
              </span>
              <h2>{sceneState.title}</h2>
              <p>{sceneState.message}</p>
              {sceneState.kind === "error" && (
                <>
                  <div className="scene-state-card__actions">
                    <button type="button" onClick={retryScene}>
                      Перезапустить 3D
                    </button>
                    <button
                      className="is-secondary"
                      type="button"
                      onClick={reloadWithFreshAssets}
                    >
                      Обновить конфигуратор
                    </button>
                  </div>
                  <details>
                    <summary>Техническая информация</summary>
                    <pre>{diagnosticText}</pre>
                  </details>
                </>
              )}
              <small>
                Версия {APP_VERSION} · {BUILD_LABEL} · {BUILD_ID}
              </small>
            </section>
          </div>
        )}

        {sceneState.kind === "ready" && assetWarnings.length > 0 && (
          <div className="scene-asset-warning" role="status">
            <div>
              <strong>Часть декора не загрузилась</strong>
              <span>{assetWarnings[0]?.message}</span>
            </div>
            <button type="button" onClick={reloadWithFreshAssets}>
              Обновить
            </button>
          </div>
        )}

        {sceneState.kind === "ready" && doorObstruction && (
          <div className="scene-door-notice" role="status" aria-live="polite">
            <div>
              <strong>Открывание ограничено</strong>
              <p>{doorObstruction}</p>
            </div>
            <button type="button" aria-label="Скрыть подсказку об открывании"
              onClick={() => setDoorObstruction(null)}>×</button>
          </div>
        )}

        {!presentationMode && (
          <div className="scene-hint">
            <span className="scene-hint__dot" />
            Корпус — влево/вправо · наполнение — вверх/вниз
          </div>
        )}
      </div>
    );
  }
);
